import { SUBSCRIPTION_RANKING } from "../../../../../../constants/subscriptionRanking";
import { OfferingCode } from "../../../../../../models";

const rankMap = new Map<string, number>();
const knownOfferings = new Set<string>();
SUBSCRIPTION_RANKING.forEach((item, index) => {
    rankMap.set(item.offeringCode, index);
    knownOfferings.add(item.offeringCode);
});

const normalizeOfferingCode = (code: string): string => (code?.includes(".") ? code : `${code}.REGULAR`);

/**
 * Determines whether a subscription would get family discount if added alongside the given existing offerings.
 * The most expensive subscription (highest rank) does NOT get the discount — all others do.
 * Mirrors the server-side logic in server/data/cart/builderFamliyLines.ts
 */
export const wouldGetFamilyDiscount = (offeringCode: string, offeringCodesInCartAndAccount: string[]): boolean => {
    const normalized = normalizeOfferingCode(offeringCode);
    const allNormalized = [normalized, ...offeringCodesInCartAndAccount.map(normalizeOfferingCode)];
    const known = allNormalized.filter((code) => knownOfferings.has(code));

    if (known.length < 2) return false;

    const sorted = [...known].sort((a, b) => (rankMap.get(b) ?? 0) - (rankMap.get(a) ?? 0));
    return sorted.lastIndexOf(normalized) > 0;
};

/**
 * Computes family discount eligibility for an ordered list of existing account subscriptions.
 * Unlike wouldGetFamilyDiscount (designed for evaluating a single new subscription), this handles
 * the case where multiple subscriptions share the same top-ranked offering code: only one is the
 * "main" (no discount), and that is the first one in display order (highest index).
 *
 * When a subscription's phone number is present in `cartPhoneToOfferingCode`, its offering code
 * is replaced with the cart item's offering code for the calculation — reflecting that the
 * subscription has been changed and should be evaluated with its new code.
 */
export const getAccountFamilyDiscounts = (
    accountSubscriptions: Array<{
        offeringCode?: string;
        priceLines?: string | number;
        phoneNumber?: string;
        subscriptionRank?: number;
    }>,
    cartOfferingCodes: string[],
    cartPhoneNumbersToOfferingCode?: Record<string, OfferingCode | undefined>,
): boolean[] => {
    // Sort a working index list by subscriptionRank so rank-1 is always chosen as main
    // when two subscriptions share the same top-ranked offering code.
    const sortedIndices = accountSubscriptions
        .map((_, i) => i)
        .sort((a, b) => {
            const ra = accountSubscriptions[a].subscriptionRank ?? Number.MAX_SAFE_INTEGER;
            const rb = accountSubscriptions[b].subscriptionRank ?? Number.MAX_SAFE_INTEGER;
            return ra - rb;
        });

    const normalizedSubCodes = accountSubscriptions.map((sub) => {
        const offeringCode = (sub.phoneNumber && cartPhoneNumbersToOfferingCode?.[sub.phoneNumber]) || sub.offeringCode;
        return offeringCode ? normalizeOfferingCode(offeringCode) : null;
    });
    const normalizedCartCodes = cartOfferingCodes.map(normalizeOfferingCode);

    const knownSubCodes = normalizedSubCodes.filter(
        (code): code is string => code !== null && knownOfferings.has(code),
    );
    const knownCartCodes = normalizedCartCodes.filter((code) => knownOfferings.has(code));

    if (knownSubCodes.length + knownCartCodes.length < 2) {
        return accountSubscriptions.map(() => false);
    }

    // Find the top-ranked offering code across all subscriptions (account + cart)
    const allKnown = [...knownSubCodes, ...knownCartCodes];
    const sorted = [...allKnown].sort((a, b) => (rankMap.get(b) ?? 0) - (rankMap.get(a) ?? 0));
    const topCode = sorted[0];

    // The "main" (no discount) is determined by subscriptionRank from CDS when available —
    // this is authoritative even for discontinued subscriptions not in our SUBSCRIPTION_RANKING list.
    // Fall back to topCode + sortedIndices when no subscriptionRanks are set.
    let mainIndex = -1;
    const subsWithRank = accountSubscriptions
        .map((sub, i) => ({ sub, i }))
        .filter(({ sub }) => sub.subscriptionRank != null);

    if (subsWithRank.length > 0) {
        // If topCode appears only in cart (not in any account sub), all account subs get discount
        const topCodeInAccountSubs = normalizedSubCodes.some((c) => c === topCode);
        if (topCodeInAccountSubs) {
            // Use the subscription with the lowest subscriptionRank as main (mirrors server-side logic)
            const mainEntry = subsWithRank.reduce((min, entry) =>
                entry.sub.subscriptionRank! < min.sub.subscriptionRank! ? entry : min,
            );
            mainIndex = mainEntry.i;
        }
        // else: mainIndex stays -1 → all account subs get discount
    } else {
        for (const i of sortedIndices) {
            if (normalizedSubCodes[i] === topCode) {
                mainIndex = i;
                break;
            }
        }
    }

    return accountSubscriptions.map((sub, i) => {
        const normalized = normalizedSubCodes[i];
        if (!sub.priceLines || !normalized || !knownOfferings.has(normalized)) return false;
        return i !== mainIndex;
    });
};
